import React, { useCallback, useEffect } from 'react';
import { Box, Stack, Typography, CircularProgress } from '@mui/material';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import Checkbox from '@mui/material/Checkbox';
import { useUpdateTaskMutation } from '@/features/engagements/engagementsApi';
import { PriorityText } from '@/components/PriorityText';
import parse from 'html-react-parser';
import { useObjectContext } from '@/hooks/useObjectContext';
import { useDispatch } from 'react-redux';
import { openDialog } from '@/features/dialog/dialogSlice';
import { showToast } from '@/features/toast/toastSlice';
import SafeHtmlContent from '../SafeHtmlContent';

export const TaskCard = ({ task }) => {
	const { dealRow, clientRow } = useObjectContext();

	const [updateTask, { isLoading }] = useUpdateTaskMutation();
	const dispatch = useDispatch();

	const completeTask = async () => {
		try {
			await updateTask({
				dealId: dealRow?.dealId,
				clientId: clientRow?.clientId,
				objectType: task.objectType ?? dealRow?.objectType ?? (clientRow ? 'client' : null),
				taskId: task.id,
				properties: { status: task.completed ? 'NOT_STARTED' : 'COMPLETED' },
			}).unwrap();
		} catch (err) {
			console.error('Error updating task', err);
			dispatch(
				showToast({
					message: `Error updating task: ${err?.error ?? 'Please try again'}`,
					error: true,
				}),
			);
		}
	};

	const handleTaskOpen = () => {
		const engagement = {
			...task,
			client: clientRow,
		};
		dispatch(openDialog({ dialogName: 'task', data: { engagement } }));
	};

	const NoteContent = useCallback(() => {
		const html = task.body ?? '';
		if (!html) return <></>;
		return <SafeHtmlContent html={html} />;
	}, [task.body]);

	return (
		<Stack width='100%' direction='row' alignItems='center' padding='0.5em 0'>
			{isLoading ? (
				<Box margin='1em'>
					<CircularProgress size='2em' />
				</Box>
			) : (
				<Checkbox
					sx={{ '& .MuiSvgIcon-root': { fontSize: '2.5em' } }}
					icon={
						<>
							<svg width={0} height={0}>
								<linearGradient
									id={`linearColors${task.id}-task-accordion-uncheck`}
									x1={0}
									y1={1}
									x2={1}
									y2={1}
								>
									<stop offset={0} stopColor='rgba(131,214,255,1)' />
									<stop offset={1} stopColor='rgba(229,132,255,1)' />
								</linearGradient>
							</svg>
							<CheckCircleOutlineRoundedIcon
								sx={{ fill: `url(#linearColors${task.id}-task-accordion-uncheck)` }}
							/>
						</>
					}
					checkedIcon={
						<>
							<svg width={0} height={0}>
								<linearGradient
									id={`linearColors${task.id}-task-accordion-check`}
									x1={0}
									y1={1}
									x2={1}
									y2={1}
								>
									<stop offset={0} stopColor='rgba(131,214,255,1)' />
									<stop offset={1} stopColor='rgba(229,132,255,1)' />
								</linearGradient>
							</svg>
							<CheckCircleRoundedIcon
								sx={{ fill: `url(#linearColors${task.id}-task-accordion-check)` }}
							/>
						</>
					}
					checked={task.completed}
					onChange={() => completeTask(task)}
				/>
			)}
			<Stack width='100%'>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
					}}
				>
					<Typography
						sx={(theme) => ({ color: theme.palette.primary.main, cursor: 'pointer' })}
						onClick={handleTaskOpen}
					>
						{task.subject}
					</Typography>
					{task.priority !== 'LOW' && task.priority !== 'NONE' && (
						<PriorityText task={task} />
					)}
				</div>

				<Typography variant='avatar_subtitle' sx={{ margin: '0' }}>
					<NoteContent />
				</Typography>
			</Stack>
		</Stack>
	);
};
